import {
  ORDER_EVENT_STATUS_DRAFT,
  ORDER_EVENT_STATUS_PUBLISHED,
  ORDER_EVENT_STATUS_ACCEPTED,
  ORDER_EVENT_STATUS_REJECTED,
  ORDER_EVENT_STATUS_VOIDED
} from '@klear/constants/order-event-status'
import { PERMISSION_ENTITY_PURCHASE_ORDER } from '@klear/constants'
import { KlearDate } from '@klear/date'
import { Money } from '@klear/money'

import isNumber from 'lodash/isNumber'

import type { PurchaseOrderCreateWire } from '@klear/purchase-order/purchase-order.wire'

export type PurchaseOrderEventSnapshot = Omit<PurchaseOrderCreateWire, 'orderId'> & { type: string; id?: number }

export type PurchaseOrderJson = {
  orderId: number
  id?: number
  idDisplay?: string
  status?: string
  number?: string
  amount: Money
  effectiveDate: KlearDate
  endDate: KlearDate
  deliveredDate?: KlearDate
  incoterm?: string
  notes?: string
}

export class PurchaseOrder {
  static readonly __klearClass = 'PurchaseOrder'

  readonly orderId: number
  id?: number
  idDisplay?: string
  status: string
  number?: string
  amount: Money
  effectiveDate: KlearDate
  endDate: KlearDate
  deliveredDate?: KlearDate
  incoterm?: string
  notes?: string

  constructor(props: PurchaseOrderJson) {
    this.orderId = props.orderId
    this.id = props.id
    this.idDisplay = props.idDisplay
    this.status = props.status ?? ORDER_EVENT_STATUS_DRAFT
    this.number = props.number
    this.amount = props.amount
    this.effectiveDate = props.effectiveDate
    this.endDate = props.endDate
    this.deliveredDate = props.deliveredDate
    this.incoterm = props.incoterm
    this.notes = props.notes
  }

  toJSON(): PurchaseOrderJson {
    return {
      orderId: this.orderId,
      id: this.id,
      idDisplay: this.idDisplay,
      status: this.status,
      number: this.number,
      amount: this.amount,
      effectiveDate: this.effectiveDate,
      endDate: this.endDate,
      deliveredDate: this.deliveredDate,
      incoterm: this.incoterm,
      notes: this.notes
    }
  }

  get isPersisted(): boolean {
    return isNumber(this.id) && this.id > 0
  }

  static isRejectedStatus(status: string): boolean {
    return status === ORDER_EVENT_STATUS_REJECTED
  }

  static isVoidedStatus(status: string): boolean {
    return status === ORDER_EVENT_STATUS_VOIDED
  }

  static isDraftStatus(status: string): boolean {
    return status === ORDER_EVENT_STATUS_DRAFT
  }

  static isPublishedStatus(status: string): boolean {
    return status === ORDER_EVENT_STATUS_PUBLISHED
  }

  static isAcceptedStatus(status: string): boolean {
    return status === ORDER_EVENT_STATUS_ACCEPTED
  }

  static readonly PLACEHOLDER_NOTES = 'Placeholder until the customer purchase order arrives.'

  static isPlaceholder(purchaseOrder: { amount: Money; number?: string | null; notes?: string | null }): boolean {
    return (
      purchaseOrder.notes === PurchaseOrder.PLACEHOLDER_NOTES &&
      purchaseOrder.amount.isZero() &&
      !purchaseOrder.number?.trim()
    )
  }

  get isRejected(): boolean {
    return PurchaseOrder.isRejectedStatus(this.status)
  }

  get isVoided(): boolean {
    return PurchaseOrder.isVoidedStatus(this.status)
  }

  get isDraft(): boolean {
    return PurchaseOrder.isDraftStatus(this.status)
  }

  get isPublished(): boolean {
    return PurchaseOrder.isPublishedStatus(this.status)
  }

  get isAccepted(): boolean {
    return PurchaseOrder.isAcceptedStatus(this.status)
  }

  assertValid(): void {
    if (!(this.orderId > 0)) {
      throw new Error('Purchase order requires an order id')
    }
    if (this.amount.lessThan(this.amount.zero)) {
      throw new Error('Purchase order amount must be zero or greater')
    }
  }
}

function parseDate(value: string, fallback: KlearDate): KlearDate {
  return KlearDate.parse(value) ?? fallback
}

export function purchaseOrderFromEventData(
  orderId: number,
  data: Omit<PurchaseOrderCreateWire, 'orderId'>,
  ctx?: { id?: number; fallbackAmount?: Money }
): PurchaseOrder {
  const effectiveDate = parseDate(data.effectiveDate, KlearDate.today())
  const endDate = parseDate(data.endDate, effectiveDate)

  return new PurchaseOrder({
    orderId,
    id: ctx?.id,
    number: data.number ?? undefined,
    amount: new Money(data.amount),
    effectiveDate,
    endDate,
    deliveredDate: data.deliveredDate == null ? undefined : (KlearDate.parse(data.deliveredDate) ?? undefined),
    incoterm: data.incoterm ?? undefined,
    notes: data.notes ?? undefined
  })
}

export function purchaseOrderToEventData(purchaseOrder: PurchaseOrder): PurchaseOrderEventSnapshot {
  const data: PurchaseOrderEventSnapshot = {
    type: PERMISSION_ENTITY_PURCHASE_ORDER,
    amount: purchaseOrder.amount.toJSON(),
    effectiveDate: purchaseOrder.effectiveDate.toJSON(),
    endDate: purchaseOrder.endDate.toJSON()
  }

  if (purchaseOrder.id != null && purchaseOrder.id > 0) data.id = purchaseOrder.id
  if (purchaseOrder.deliveredDate != null) data.deliveredDate = purchaseOrder.deliveredDate.toJSON()
  if (purchaseOrder.number != null) data.number = purchaseOrder.number
  if (purchaseOrder.incoterm != null) data.incoterm = purchaseOrder.incoterm
  if (purchaseOrder.notes != null) data.notes = purchaseOrder.notes

  return data
}
